// LearnCore Frontend - 主应用组件

import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import SidebarLayout from './components/SidebarLayout';
import { PageLoading } from './components/Loading';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import OfflineIndicator from './components/OfflineIndicator';

// 页面组件
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ChildLoginPage from './pages/ChildLoginPage';
import DashboardPage from './pages/DashboardPage';
import CreateSetPage from './pages/CreateSetPage';
import SetViewPage from './pages/SetViewPage';
import FlashcardModePage from './pages/FlashcardModePage';
import TermQuizPage from './pages/TermQuizPage';
import DefinitionQuizPage from './pages/DefinitionQuizPage';
import EditSetPage from './pages/EditSetPage';
import UserCenterPage from './pages/UserCenterPage';
import MarketplacePage from './pages/MarketplacePage';
import StudyTimePage from './pages/StudyTimePage';
import TestRichTextPage from './pages/TestRichTextPage';
import ReadingPassagesPage from './pages/ReadingPassagesPage';
import CreatePassagePage from './pages/CreatePassagePage';
import EditPassagePage from './pages/EditPassagePage';
import StudyPassagePage from './pages/StudyPassagePage';
import ClassicalChineseListPage from './pages/ClassicalChineseListPage';
import CreateClassicalChinesePage from './pages/CreateClassicalChinesePage';
import EditClassicalChinesePage from './pages/EditClassicalChinesePage';
import StudyClassicalChinesePage from './pages/StudyClassicalChinesePage';

// 任务和奖励系统页面
import TasksPage from './pages/TasksPage';
import RewardsPage from './pages/RewardsPage';
import CalendarPage from './pages/CalendarPage';
import ParentDashboardPage from './pages/ParentDashboardPage';
import ParentRegisterPage from './pages/ParentRegisterPage';

// 保护路由组件 - 需要登录才能访问
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoading />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// 访客路由组件 - 已登录用户自动跳转到仪表盘
function GuestRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  
  // 检查 URL 中是否有 redirect 参数（用于跨应用登录）
  const searchParams = new URLSearchParams(window.location.search);
  const redirectUrl = searchParams.get('redirect');

  if (loading) {
    return <PageLoading />;
  }

  if (user) {
    // 如果有 redirect 参数且是 mindcraft.win 域名，跳转到该 URL
    if (redirectUrl) {
      try {
        const url = new URL(redirectUrl);
        if (url.hostname.endsWith('.mindcraft.win') || url.hostname === 'mindcraft.win') {
          window.location.href = redirectUrl;
          return <PageLoading />; // 显示加载状态等待跳转
        }
      } catch {
        // URL 解析失败，忽略
      }
    }
    return <Navigate to="/tasks" replace />;
  }

  return <>{children}</>;
}

// 应用路由
function AppRoutes() {
  return (
    <SidebarLayout>
      <Routes>
        {/* 公开路由 */}
        <Route path="/" element={<HomePage />} />
        <Route path="/marketplace" element={<MarketplacePage />} />
        <Route path="/sets/:id/view" element={<SetViewPage />} />
        <Route path="/sets/:id/study" element={<FlashcardModePage />} />
        <Route path="/sets/:id/quiz/term" element={<TermQuizPage />} />
        <Route path="/sets/:id/quiz/definition" element={<DefinitionQuizPage />} />
        <Route path="/passages/:id/view" element={<StudyPassagePage />} />
        <Route path="/passages/:id/study" element={<StudyPassagePage />} />
        <Route path="/classical-chinese/:id" element={<StudyClassicalChinesePage />} />

        {/* 访客路由 - 已登录用户会被重定向 */}
        <Route path="/login" element={
          <GuestRoute>
            <LoginPage />
          </GuestRoute>
        } />
        <Route path="/register" element={
          <GuestRoute>
            <RegisterPage />
          </GuestRoute>
        } />
        <Route path="/child/login" element={
          <GuestRoute>
            <ChildLoginPage />
          </GuestRoute>
        } />
        <Route path="/parent/register" element={
          <GuestRoute>
            <ParentRegisterPage />
          </GuestRoute>
        } />

        {/* 保护路由 - 需要登录 */}
        <Route path="/flashcard" element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } />
        <Route path="/sets/create" element={
          <ProtectedRoute>
            <CreateSetPage />
          </ProtectedRoute>
        } />
        <Route path="/sets/:id/edit" element={
          <ProtectedRoute>
            <EditSetPage />
          </ProtectedRoute>
        } />
        <Route path="/user/center" element={
          <ProtectedRoute>
            <UserCenterPage />
          </ProtectedRoute>
        } />
        <Route path="/study-time" element={
          <ProtectedRoute>
            <StudyTimePage />
          </ProtectedRoute>
        } />
        <Route path="/passages" element={
          <ProtectedRoute>
            <ReadingPassagesPage />
          </ProtectedRoute>
        } />
        <Route path="/passages/create" element={
          <ProtectedRoute>
            <CreatePassagePage />
          </ProtectedRoute>
        } />
        <Route path="/passages/:id/edit" element={
          <ProtectedRoute>
            <EditPassagePage />
          </ProtectedRoute>
        } />
        <Route path="/classical-chinese" element={
          <ProtectedRoute>
            <ClassicalChineseListPage />
          </ProtectedRoute>
        } />
        <Route path="/classical-chinese/create" element={
          <ProtectedRoute>
            <CreateClassicalChinesePage />
          </ProtectedRoute>
        } />
        <Route path="/classical-chinese/:id/edit" element={
          <ProtectedRoute>
            <EditClassicalChinesePage />
          </ProtectedRoute>
        } />

        {/* 任务和奖励系统路由 */}
        <Route path="/tasks" element={
          <ProtectedRoute>
            <TasksPage />
          </ProtectedRoute>
        } />
        <Route path="/rewards" element={
          <ProtectedRoute>
            <RewardsPage />
          </ProtectedRoute>
        } />
        <Route path="/calendar" element={
          <ProtectedRoute>
            <CalendarPage />
          </ProtectedRoute>
        } />
        <Route path="/parent" element={
          <ProtectedRoute>
            <ParentDashboardPage />
          </ProtectedRoute>
        } />
        <Route path="/parent/register" element={
          <GuestRoute>
            <ParentRegisterPage />
          </GuestRoute>
        } />

        {/* 开发测试路由 */}
        {import.meta.env.DEV && (
          <Route path="/test/richtext" element={<TestRichTextPage />} />
        )}

        {/* 404 页面 */}
        <Route path="*" element={
          <div className="min-h-screen flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-6xl font-bold text-gray-300 mb-4">404</h1>
              <p className="text-xl text-gray-600 mb-8">页面未找到</p>
              <a href="/" className="btn-primary">
                返回首页
              </a>
            </div>
          </div>
        } />
      </Routes>
    </SidebarLayout>
  );
}

// 主应用组件
function App() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Router>
        <AuthProvider>
          {/* PWA组件 */}
          <PWAInstallPrompt />
          <OfflineIndicator />
          <AppRoutes />
        </AuthProvider>
      </Router>
    </div>
  );
}

export default App;
